import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { getMediaUrl } from '../../services/media';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import styles from '../Books/BookForm.module.css';

const emptyForm = { name: '', role: '', birthYear: '', deathYear: '', description: '' };

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 1899 }, (_, i) => {
  const year = CURRENT_YEAR - i;
  return { value: String(year), label: String(year) };
});
const DEATH_YEAR_OPTIONS = [{ value: '', label: 'Жив(а)' }, ...YEAR_OPTIONS];

const PersonForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      api.get(`/persons/${id}`).then((res) => {
        const p = res.data;
        setForm({
          name: p.name || '',
          role: p.role || '',
          birthYear: p.birthYear || '',
          deathYear: p.deathYear || '',
          description: p.description || '',
        });
        if (p.imageUrl) setImagePreview(getMediaUrl(p.imageUrl));
      }).catch(() => {}).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => formData.append(key, val));
      if (imageFile) formData.append('image', imageFile);

      if (isEdit) {
        await api.put(`/persons/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/persons', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      navigate('/persons');
    } catch {
      setError('Ошибка при сохранении.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.loading}>Загрузка...</div>;

  return (
    <div className={styles.page}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.section}>
          <div className={styles.grid}>
            <div className={`${styles.field} ${styles.fullWidth}`}>
              <label className={styles.label}>Имя *</label>
              <input className={styles.input} name="name" value={form.name} onChange={handleChange} required placeholder="Полное имя" />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Роль</label>
              <input className={styles.input} name="role" value={form.role} onChange={handleChange} placeholder="Писатель, Поэт..." />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Год рождения</label>
              <CustomSelect
                value={form.birthYear}
                onChange={(val) => setForm((prev) => ({ ...prev, birthYear: val }))}
                options={YEAR_OPTIONS}
                placeholder="Выберите год"
                searchable
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Год смерти</label>
              <CustomSelect
                value={form.deathYear}
                onChange={(val) => setForm((prev) => ({ ...prev, deathYear: val }))}
                options={DEATH_YEAR_OPTIONS}
                placeholder="Жив(а) / выберите год"
                searchable
              />
            </div>

            <div className={`${styles.field} ${styles.fullWidth}`}>
              <label className={styles.label}>Биография</label>
              <textarea className={styles.textarea} name="description" value={form.description} onChange={handleChange} placeholder="Краткая биография..." style={{ minHeight: 120 }} />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.label}>Фото</label>
              <div className={styles.imagePicker}>
                <div className={styles.imagePreviewBox}>
                  {imagePreview ? (
                    <img src={imagePreview} alt="Фото" />
                  ) : (
                    <div className={styles.imagePlaceholder}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                  )}
                </div>
                <button type="button" className={styles.imagePickButton}>
                  Выбрать фото
                  <input type="file" accept="image/*" className={styles.fileInput} onChange={handleImageChange} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button type="submit" className={styles.saveBtn} disabled={saving}>
            {saving ? 'Сохранение...' : (isEdit ? 'Сохранить' : 'Добавить персону')}
          </button>
          <Link to="/persons" className={styles.cancelBtn}>Отмена</Link>
        </div>
      </form>
    </div>
  );
};

export default PersonForm;
